import { Action, Dispatch, Reducer } from 'redux';
import { EmptyRequestState, RequestState } from '../../models/RequestState';
import InstanceClient from '../../services/InstanceClient';
import StatusCode from '../../util/StatusCode';
import { AddClassroomMembers, AddedClassroomMember, CoreClassroomAdminCourseState } from '../../models/CoreClassroomAdminClassroom';

enum ClassroomToolsActionType {
    DELETE_CLASSROOM = 'DELETE_CLASSROOM',
    UPDATE_OWNER = 'UPDATE_OWNER',
    ADD_MEMBER = 'ADD_MEMBER',
    CHANGE_STATUS = 'CHANGE_STATUS'
}

export interface ClassroomToolsState {
    deleteClassroom: EmptyRequestState
    updateOwnerClassroom: EmptyRequestState
    addMembersClassroom: RequestState<AddedClassroomMember[]>
    changeStatusClassroom: EmptyRequestState
}

interface ClassroomTools_DeleteClassroom extends Action<ClassroomToolsActionType.DELETE_CLASSROOM> {
    deleteClassroom: EmptyRequestState
}

interface ClassroomTools_UpdateOwnerClassroom extends Action<ClassroomToolsActionType.UPDATE_OWNER> {
    updateOwnerClassroom: EmptyRequestState
}

interface ClassroomTools_AddMembersClassroom extends Action<ClassroomToolsActionType.ADD_MEMBER> {
    addMembersClassroom: RequestState<AddedClassroomMember[]>
}

interface ClassroomTools_ChangeStatusClassroom extends Action<ClassroomToolsActionType.CHANGE_STATUS> {
    changeStatusClassroom: EmptyRequestState
}

type ClassroomToolsAction = ClassroomTools_DeleteClassroom
    | ClassroomTools_UpdateOwnerClassroom
    | ClassroomTools_AddMembersClassroom
    | ClassroomTools_ChangeStatusClassroom;

export const DeleteClassroom = (classroomId: string) => (dispatch: Dispatch<ClassroomTools_DeleteClassroom>) => {
    dispatch({
        type: ClassroomToolsActionType.DELETE_CLASSROOM,
        deleteClassroom: { code: StatusCode.PENDING }
    });

    return InstanceClient.delete(`/api/core/classroomadmin/classroom/tools/${classroomId}`)
        .then(() => {
            dispatch({
                type: ClassroomToolsActionType.DELETE_CLASSROOM,
                deleteClassroom: { code: StatusCode.COMPLETE }
            });

            return { classroomId, code: StatusCode.COMPLETE };
        })
        .catch(error => {
            dispatch({
                type: ClassroomToolsActionType.DELETE_CLASSROOM,
                deleteClassroom: { code: StatusCode.ERROR, error }
            });

            return { classroomId, code: StatusCode.ERROR, error };
        });
};

export const ClearDeleteClassroom = () => (dispatch: Dispatch<ClassroomTools_DeleteClassroom>) => {
    dispatch({
        type: ClassroomToolsActionType.DELETE_CLASSROOM,
        deleteClassroom: { code: StatusCode.NONE }
    });
};

export const ClassroomUpdateOwner = (classroomId: string, newOwner: string) => (dispatch: Dispatch<ClassroomTools_UpdateOwnerClassroom>) => {
    dispatch({
        type: ClassroomToolsActionType.UPDATE_OWNER,
        updateOwnerClassroom: { code: StatusCode.PENDING }
    });

    return InstanceClient.put(`/api/core/classroomadmin/classroom/tools/owner/${classroomId}`, { newOwner })
        .then(() => {
            dispatch({
                type: ClassroomToolsActionType.UPDATE_OWNER,
                updateOwnerClassroom: { code: StatusCode.COMPLETE }
            });

            return { classroomId, code: StatusCode.COMPLETE };
        })

        .catch(error => {
            dispatch({
                type: ClassroomToolsActionType.UPDATE_OWNER,
                updateOwnerClassroom: { code: StatusCode.ERROR, error }
            });

            return { classroomId, code: StatusCode.ERROR, error };
        });
};

export const ClearClassroomUpdateOwner = () => (dispatch: Dispatch<ClassroomTools_UpdateOwnerClassroom>) => {
    dispatch({
        type: ClassroomToolsActionType.UPDATE_OWNER,
        updateOwnerClassroom: { code: StatusCode.NONE }
    });
}

export const ClassroomAddMembers = (classroomId: string, classroomMembers: AddClassroomMembers) => (dispatch: Dispatch<ClassroomTools_AddMembersClassroom>) => {
    dispatch({
        type: ClassroomToolsActionType.ADD_MEMBER,
        addMembersClassroom: { code: StatusCode.PENDING }
    });

    return InstanceClient.put<AddedClassroomMember[], AddClassroomMembers>(`/api/core/classroomadmin/classroom/tools/members/${classroomId}`, classroomMembers)
        .then(res => {
            dispatch({
                type: ClassroomToolsActionType.ADD_MEMBER,
                addMembersClassroom: { code: StatusCode.COMPLETE, data: res.data }
            })
            return { classroomId, code: StatusCode.COMPLETE, data: res.data };
        })
        .catch(error => {
            dispatch({
                type: ClassroomToolsActionType.ADD_MEMBER,
                addMembersClassroom: { code: StatusCode.ERROR, error }
            })
            return { classroomId, code: StatusCode.ERROR, error };
        });
};

export const ClearClassroomAddMembers = () => (dispatch: Dispatch<ClassroomTools_AddMembersClassroom>) => {
    dispatch({
        type: ClassroomToolsActionType.ADD_MEMBER,
        addMembersClassroom: { code: StatusCode.NONE }
    });
};

export const ClassroomChangeStatus = (classroomId: string, newStatus: CoreClassroomAdminCourseState) => (dispatch: Dispatch<ClassroomTools_ChangeStatusClassroom>) => {
    dispatch({
        type: ClassroomToolsActionType.CHANGE_STATUS,
        changeStatusClassroom: { code: StatusCode.PENDING }
    });

    return InstanceClient.send<void, CoreClassroomAdminCourseState>({
        method: 'PUT',
        url: `/api/core/classroomadmin/classroom/tools/status/${classroomId}`,
        data: newStatus,
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then(() => {
            dispatch({
                type: ClassroomToolsActionType.CHANGE_STATUS,
                changeStatusClassroom: { code: StatusCode.COMPLETE }
            });

            return { classroomId, code: StatusCode.COMPLETE };

        })
        .catch(error => {
            dispatch({
                type: ClassroomToolsActionType.CHANGE_STATUS,
                changeStatusClassroom: { code: StatusCode.ERROR, error }
            });

            return { classroomId, code: StatusCode.ERROR, error };

        });
};

export const ClearClassroomChangeStatus = () => (dispatch: Dispatch<ClassroomTools_ChangeStatusClassroom>) => {
    dispatch({
        type: ClassroomToolsActionType.CHANGE_STATUS,
        changeStatusClassroom: { code: StatusCode.NONE }
    });
};

const initialState: ClassroomToolsState = {
    deleteClassroom: { code: StatusCode.NONE },
    updateOwnerClassroom: { code: StatusCode.NONE },
    addMembersClassroom: { code: StatusCode.NONE },
    changeStatusClassroom: { code: StatusCode.NONE }
};

export const reducer: Reducer<ClassroomToolsState, ClassroomToolsAction> = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case ClassroomToolsActionType.DELETE_CLASSROOM:
            return {
                ...state,
                deleteClassroom: action.deleteClassroom
            };
        case ClassroomToolsActionType.UPDATE_OWNER:
            return {
                ...state,
                updateOwnerClassroom: action.updateOwnerClassroom
            };
        case ClassroomToolsActionType.ADD_MEMBER:
            return {
                ...state,
                addMembersClassroom: action.addMembersClassroom
            };
        case ClassroomToolsActionType.CHANGE_STATUS:
            return {
                ...state,
                changeStatusClassroom: action.changeStatusClassroom
            };
        default:
            return state;
    }
};